// Stacked-bar mana curve viz for the saved-deck Mana Base panel.
// X axis: nonland CMC buckets 0..7+. Stacks toggle between two views:
//   - "type"  → Creature / Instant / Sorcery / Enchantment / Artifact /
//               Planeswalker / Battle / Other (card type breakdown).
//   - "color" → W / U / B / R / G / C (color *symbol* breakdown — pip
//               counts from each card's mana_cost weighted by the
//               card's count, so a 4× {1}{U}{U} contributes 8 U pips).
//
// SVG-native (no recharts) for a small bundle and full theming control.
// Aesthetic targets: shadcn charts / Linear / Vercel analytics —
// gradient stack segments, soft hover glow with peer dimming, dashed
// gridlines, custom React tooltip, sliding-indicator toggle.

import { useMemo, useRef, useState } from "react";
import type { DeckEntry, CardMeta } from "../lib/api";
import { cn } from "../lib/cn";

type StackMode = "type" | "color";
type ChartStyle = "bar" | "line";

const BUCKETS = [0, 1, 2, 3, 4, 5, 6, 7] as const;

// Stack order is bottom-to-top — Creature reads as the foundation.
// Lands are excluded from the curve entirely (no "Land" key).
const TYPE_KEYS = [
  "Creature",
  "Planeswalker",
  "Battle",
  "Instant",
  "Sorcery",
  "Enchantment",
  "Artifact",
  "Other",
] as const;
type TypeKey = (typeof TYPE_KEYS)[number];

// Type palette. Deliberately distinct from MTG mana colors so the
// "Type" view doesn't read as a color view — uses a teal/coral/violet
// triad for the dominant card classes (Creature/Instant/Sorcery) and
// neutralizes the rest.
const TYPE_COLOR: Record<TypeKey, [string, string]> = {
  Creature: ["#3FB8AF", "#2FA09A"],     // teal
  Instant: ["#F08A6E", "#DC7459"],       // coral
  Sorcery: ["#A285DC", "#8E6FCC"],       // violet
  Planeswalker: ["#E8B14A", "#D49C36"],  // amber
  Battle: ["#E25C7A", "#CC4866"],        // magenta
  Enchantment: ["#FFD089", "#EBBA72"],   // pale gold
  Artifact: ["#9CA1A8", "#86898E"],      // graphite
  Other: ["#6A6A6A", "#5A5A5A"],         // fallback gray
};

const COLOR_KEYS = ["W", "U", "B", "R", "G", "C"] as const;
type ColorKey = (typeof COLOR_KEYS)[number];

// MTG color palette. Black's bottom stops at #1F1B1B — never pure black,
// to keep the segment readable on dark surfaces.
const COLOR_PALETTE: Record<ColorKey, [string, string]> = {
  W: ["#F5EDC9", "#E0D8B4"],
  U: ["#4A8FD4", "#3A7FC4"],
  B: ["#2F2A2A", "#1F1B1B"],
  R: ["#D5594A", "#C0493A"],
  G: ["#5C9871", "#4D8862"],
  C: ["#A8A39A", "#928D84"],
};

const COLOR_LABEL: Record<ColorKey, string> = {
  W: "White",
  U: "Blue",
  B: "Black",
  R: "Red",
  G: "Green",
  C: "Colorless",
};

interface BarSegment {
  key: string;
  count: number;
  // Gradient pair: [top, bottom]. The legend chip uses the top color.
  colorTop: string;
  colorBottom: string;
  label: string;
}

interface BarData {
  bucket: number;
  segments: BarSegment[];
  total: number;
}

function classifyType(typeLine: string): TypeKey {
  // type_line is space-delimited Scryfall types — match in priority
  // order. A "Legendary Creature — Human Soldier" still goes to
  // Creature; a "Battle — Siege" goes to Battle even though the line
  // also names "Siege" as a subtype.
  if (typeLine.includes("Creature")) return "Creature";
  if (typeLine.includes("Planeswalker")) return "Planeswalker";
  if (typeLine.includes("Battle")) return "Battle";
  if (typeLine.includes("Instant")) return "Instant";
  if (typeLine.includes("Sorcery")) return "Sorcery";
  if (typeLine.includes("Enchantment")) return "Enchantment";
  if (typeLine.includes("Artifact")) return "Artifact";
  return "Other";
}

// Count colored mana pips in a Scryfall mana_cost string. Hybrid pips
// ({W/U}) contribute 0.5 to each color so split-cost cards don't double-
// count. {2/W} (Phyrexian or hybrid-with-generic) contributes 0.5 to
// the color half; {X} and pure generic ({1}, {2}, ...) are skipped.
// Returns counts for W/U/B/R/G plus C for {C} (colorless mana symbols).
function pipCounts(manaCost: string | null): Record<ColorKey, number> {
  const out: Record<ColorKey, number> = { W: 0, U: 0, B: 0, R: 0, G: 0, C: 0 };
  if (!manaCost) return out;
  const symbols = manaCost.match(/\{([^}]+)\}/g) ?? [];
  for (const sym of symbols) {
    const inner = sym.slice(1, -1).toUpperCase();
    if (inner === "X" || /^\d+$/.test(inner)) continue;
    if (inner === "C") {
      out.C += 1;
      continue;
    }
    // Hybrid (e.g. W/U, 2/W, R/P). Award 0.5 to each colored half.
    if (inner.includes("/")) {
      const parts = inner.split("/");
      for (const p of parts) {
        if (p === "W" || p === "U" || p === "B" || p === "R" || p === "G") {
          out[p] += 0.5;
        }
      }
      continue;
    }
    if (inner === "W" || inner === "U" || inner === "B" || inner === "R" || inner === "G") {
      out[inner] += 1;
    }
  }
  return out;
}

export function buildCurveData(
  mainboard: DeckEntry[],
  meta: Record<string, CardMeta> | undefined,
  mode: StackMode,
): BarData[] {
  const empty: BarData[] = BUCKETS.map((bucket) => ({
    bucket,
    segments: [],
    total: 0,
  }));
  if (!meta) return empty;

  // Bucket → key → count
  const acc = new Map<number, Map<string, number>>();
  for (const b of BUCKETS) acc.set(b, new Map<string, number>());

  for (const e of mainboard) {
    const m = meta[e.name.toLowerCase()];
    if (!m) continue;
    if (m.type_line.includes("Land")) continue;
    const bucket = Math.min(7, Math.max(0, Math.round(m.cmc)));
    const target = acc.get(bucket)!;
    if (mode === "type") {
      const key = classifyType(m.type_line);
      target.set(key, (target.get(key) ?? 0) + e.count);
    } else {
      const pips = pipCounts(m.mana_cost);
      for (const k of COLOR_KEYS) {
        if (pips[k] > 0) {
          target.set(k, (target.get(k) ?? 0) + pips[k] * e.count);
        }
      }
    }
  }

  return BUCKETS.map((bucket) => {
    const map = acc.get(bucket)!;
    const order = mode === "type" ? TYPE_KEYS : COLOR_KEYS;
    const segments: BarSegment[] = [];
    for (const k of order) {
      const v = map.get(k) ?? 0;
      if (v <= 0) continue;
      const pair = mode === "type"
        ? TYPE_COLOR[k as TypeKey]
        : COLOR_PALETTE[k as ColorKey];
      segments.push({
        key: k,
        count: v,
        colorTop: pair[0],
        colorBottom: pair[1],
        label: mode === "type" ? k : COLOR_LABEL[k as ColorKey],
      });
    }
    const total = segments.reduce((n, s) => n + s.count, 0);
    return { bucket, segments, total };
  });
}

export default function ManaCurveChart({
  mainboard,
  meta,
  className,
}: {
  mainboard: DeckEntry[];
  meta: Record<string, CardMeta> | undefined;
  className?: string;
}) {
  const [mode, setMode] = useState<StackMode>("type");
  const [style, setStyle] = useState<ChartStyle>("line");
  const data = useMemo(() => buildCurveData(mainboard, meta, mode), [mainboard, meta, mode]);
  const max = Math.max(1, ...data.map((d) => d.total));
  const nonlandTotal = data.reduce((n, d) => n + d.total, 0);

  // Active segments for the legend — only show keys that actually
  // appear in the deck so the legend doesn't render six color chips
  // for a mono-red list.
  const activeSegments = useMemo(() => {
    const seen = new Map<string, BarSegment>();
    for (const bar of data) {
      for (const seg of bar.segments) {
        if (!seen.has(seg.key)) seen.set(seg.key, seg);
      }
    }
    // Stable order: stack order (TYPE_KEYS / COLOR_KEYS) keeps legend
    // chips matching the bar's bottom-to-top stacking visually.
    const order = mode === "type" ? TYPE_KEYS : COLOR_KEYS;
    const result: BarSegment[] = [];
    for (const k of order) {
      const seg = seen.get(k);
      if (seg) result.push(seg);
    }
    return result;
  }, [data, mode]);

  if (nonlandTotal === 0) {
    return null;
  }

  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex items-baseline justify-between gap-3">
        <div className="flex items-baseline gap-2">
          <h4 className="font-mono text-[10px] uppercase tracking-[0.12em] text-fg-subtle">
            Mana curve
          </h4>
          <span className="font-mono text-[10px] tabular-nums text-fg-subtle/70">
            {nonlandTotal} nonland
          </span>
        </div>
        <div className="flex items-center gap-2">
          <StyleToggle style={style} onChange={setStyle} />
          <ModeToggle mode={mode} onChange={setMode} />
        </div>
      </div>

      <Chart data={data} max={max} mode={mode} style={style} />

      {activeSegments.length > 0 && (
        <ul className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[12px] text-fg-subtle">
          {activeSegments.map((s) => (
            <li key={s.key} className="inline-flex items-center gap-1.5">
              <span
                aria-hidden
                className="inline-block h-2.5 w-2.5 rounded-[3px]"
                style={{
                  background: `linear-gradient(180deg, ${s.colorTop}, ${s.colorBottom})`,
                }}
              />
              <span>{s.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Sliding-indicator pill toggle. Two buttons live on top; a single
// rounded background slides between them with a 200ms transform.
// Pure Tailwind transitions — no animation library.
function ModeToggle({
  mode,
  onChange,
}: {
  mode: StackMode;
  onChange: (m: StackMode) => void;
}) {
  return (
    <div className="relative inline-flex items-center rounded-full border border-border/60 bg-bg-surface/40 p-0.5 text-[10px] font-medium uppercase tracking-wider">
      {/* Sliding indicator. Width is 50% of the parent's content box;
          translate from 0 → 100% to swap positions. */}
      <span
        aria-hidden
        className={cn(
          "absolute top-0.5 bottom-0.5 left-0.5 w-[calc(50%-2px)] rounded-full bg-accent/15",
          "transition-transform duration-200 ease-out",
          mode === "color" ? "translate-x-full" : "translate-x-0",
        )}
      />
      <button
        type="button"
        className={cn(
          "relative z-[1] px-3 py-1 rounded-full transition-colors",
          mode === "type" ? "text-accent" : "text-fg-subtle hover:text-fg",
        )}
        onClick={() => onChange("type")}
      >
        Type
      </button>
      <button
        type="button"
        className={cn(
          "relative z-[1] px-3 py-1 rounded-full transition-colors",
          mode === "color" ? "text-accent" : "text-fg-subtle hover:text-fg",
        )}
        onClick={() => onChange("color")}
      >
        Color
      </button>
    </div>
  );
}

// Bar ↔ Line style toggle. Same sliding-indicator pattern as ModeToggle.
function StyleToggle({
  style,
  onChange,
}: {
  style: ChartStyle;
  onChange: (s: ChartStyle) => void;
}) {
  return (
    <div className="relative inline-flex items-center rounded-full border border-border/60 bg-bg-surface/40 p-0.5 text-[10px] font-medium uppercase tracking-wider">
      <span
        aria-hidden
        className={cn(
          "absolute top-0.5 bottom-0.5 left-0.5 w-[calc(50%-2px)] rounded-full bg-accent/15",
          "transition-transform duration-200 ease-out",
          style === "line" ? "translate-x-full" : "translate-x-0",
        )}
      />
      <button
        type="button"
        className={cn(
          "relative z-[1] px-3 py-1 rounded-full transition-colors",
          style === "bar" ? "text-accent" : "text-fg-subtle hover:text-fg",
        )}
        onClick={() => onChange("bar")}
      >
        Bar
      </button>
      <button
        type="button"
        className={cn(
          "relative z-[1] px-3 py-1 rounded-full transition-colors",
          style === "line" ? "text-accent" : "text-fg-subtle hover:text-fg",
        )}
        onClick={() => onChange("line")}
      >
        Line
      </button>
    </div>
  );
}

interface HoverState {
  bucket: number;
  // Position is relative to the chart container (in CSS pixels) so the
  // tooltip can be absolutely positioned without doing extra math.
  cx: number;
  cy: number;
}

function Chart({
  data,
  max,
  mode,
  style,
}: {
  data: BarData[];
  max: number;
  mode: StackMode;
  style: ChartStyle;
}) {
  // Compact viewBox — the chart sits under the Mana Base prose as a
  // visual accent, not a primary surface. Shorter Y, narrower bars,
  // tighter paddings.
  const W = 320;
  const H = 96;
  const PAD_TOP = 10;
  const PAD_BOTTOM = 18;
  const innerH = H - PAD_TOP - PAD_BOTTOM;
  const baselineY = H - PAD_BOTTOM;
  const slotW = W / data.length;
  const barW = Math.min(18, slotW * 0.5);


  // Stable gradient IDs scoped to this chart instance so multiple
  // charts on a page don't collide.
  const idRef = useRef<string>(`mc-${Math.random().toString(36).slice(2, 9)}`);
  const uid = idRef.current;

  // Collect every distinct (key, colorTop, colorBottom) so we can emit
  // one <linearGradient> per stack key instead of one per segment.
  const gradients = useMemo(() => {
    const seen = new Map<string, { top: string; bottom: string }>();
    for (const bar of data) {
      for (const seg of bar.segments) {
        if (!seen.has(seg.key)) {
          seen.set(seg.key, { top: seg.colorTop, bottom: seg.colorBottom });
        }
      }
    }
    return seen;
  }, [data]);

  // Pivot stacked data → one series per key for line mode, with a
  // count per bucket (zero-filled). Order matches the order keys
  // first appear in `data` (which itself follows the stack-order
  // constants), so the legend chip order matches the line draw order.
  interface Series {
    key: string;
    label: string;
    colorTop: string;
    colorBottom: string;
    points: { bucket: number; count: number }[];
  }
  const series = useMemo<Series[]>(() => {
    const keyOrder: string[] = [];
    const seen = new Map<string, Series>();
    for (const bar of data) {
      for (const seg of bar.segments) {
        if (!seen.has(seg.key)) {
          keyOrder.push(seg.key);
          seen.set(seg.key, {
            key: seg.key,
            label: seg.label,
            colorTop: seg.colorTop,
            colorBottom: seg.colorBottom,
            points: data.map((d) => ({ bucket: d.bucket, count: 0 })),
          });
        }
      }
    }
    for (let bi = 0; bi < data.length; bi++) {
      const bar = data[bi]!;
      for (const seg of bar.segments) {
        const s = seen.get(seg.key)!;
        s.points[bi]!.count = seg.count;
      }
    }
    return keyOrder.map((k) => seen.get(k)!);
  }, [data]);

  // Line-mode y-axis: scale to the tallest single-key bucket so an
  // individual line isn't squished by an unrelated stacked total.
  const lineMax = useMemo(() => {
    let m = 0;
    for (const s of series) {
      for (const p of s.points) {
        if (p.count > m) m = p.count;
      }
    }
    return Math.max(1, m);
  }, [series]);

  const yMax = style === "line" ? lineMax : max;

  // Gridline values: yMax, yMax/2, yMax/4. Drop duplicate rounded rows.
  const gridValues = useMemo(() => {
    const candidates = [yMax, yMax / 2, yMax / 4];
    const seen = new Set<number>();
    const result: number[] = [];
    for (const v of candidates) {
      const rounded = Math.round(v * 10) / 10;
      if (rounded <= 0) continue;
      if (seen.has(rounded)) continue;
      seen.add(rounded);
      result.push(rounded);
    }
    return result;
  }, [yMax]);

  const [hover, setHover] = useState<HoverState | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Convert SVG coords → CSS pixels so the absolutely-positioned
  // tooltip lands where the bar actually is on screen.
  const toCssCoords = (svgX: number, svgY: number): { x: number; y: number } => {
    const el = containerRef.current;
    if (!el) return { x: 0, y: 0 };
    const rect = el.getBoundingClientRect();
    return {
      x: (svgX / W) * rect.width,
      y: (svgY / H) * rect.height,
    };
  };

  return (
    <div ref={containerRef} className="relative">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-auto select-none text-fg-subtle"
        role="img"
        aria-label="Stacked mana curve"
      >
        <defs>
          {/* One gradient per stack key. y1=0 / y2=1 = top→bottom. */}
          {Array.from(gradients.entries()).map(([key, pair]) => (
            <linearGradient
              key={key}
              id={`${uid}-grad-${key}`}
              x1={0}
              y1={0}
              x2={0}
              y2={1}
            >
              <stop offset="0%" stopColor={pair.top} />
              <stop offset="100%" stopColor={pair.bottom} />
            </linearGradient>
          ))}
          {/* Hover glow filter: soft Gaussian blur behind the bar. */}
          <filter
            id={`${uid}-glow`}
            x="-50%"
            y="-50%"
            width="200%"
            height="200%"
          >
            <feGaussianBlur stdDeviation="3.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Dashed gridlines (yMax, yMax/2, yMax/4). */}
        {gridValues.map((v) => {
          const y = baselineY - (v / yMax) * innerH;
          return (
            <line
              key={`grid-${v}`}
              x1={0}
              x2={W}
              y1={y}
              y2={y}
              stroke="currentColor"
              strokeOpacity={0.12}
              strokeWidth={0.5}
              strokeDasharray="2 3"
            />
          );
        })}
        {/* Solid baseline. */}
        <line
          x1={0}
          y1={baselineY}
          x2={W}
          y2={baselineY}
          stroke="currentColor"
          strokeOpacity={0.18}
        />

        {/* === LINE MODE: per-series areas + polylines (drawn under
             the hit-target layer so hover still wins). === */}
        {style === "line" && (
          <g>
            {series.map((s) => {
              const isHovered = hover !== null;
              const pts = s.points.map((p) => ({
                cx: p.bucket * slotW + slotW / 2,
                cy: baselineY - (p.count / yMax) * innerH,
              }));
              const linePath = pts
                .map((p, i) => `${i === 0 ? "M" : "L"} ${p.cx} ${p.cy}`)
                .join(" ");
              const last = pts[pts.length - 1]!;
              const first = pts[0]!;
              const areaPath =
                linePath +
                ` L ${last.cx} ${baselineY} L ${first.cx} ${baselineY} Z`;
              return (
                <g key={s.key}>
                  <path
                    d={areaPath}
                    fill={`url(#${uid}-grad-${s.key})`}
                    fillOpacity={0.15}
                  />
                  <path
                    d={linePath}
                    fill="none"
                    stroke={s.colorTop}
                    strokeWidth={1.75}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    filter={isHovered ? `url(#${uid}-glow)` : undefined}
                  />
                  {s.points.map((p, i) => {
                    if (p.count === 0) return null;
                    const pt = pts[i]!;
                    return (
                      <circle
                        key={p.bucket}
                        cx={pt.cx}
                        cy={pt.cy}
                        r={2}
                        fill={s.colorTop}
                      />
                    );
                  })}
                </g>
              );
            })}
            {hover !== null && (
              <line
                x1={hover.bucket * slotW + slotW / 2}
                x2={hover.bucket * slotW + slotW / 2}
                y1={PAD_TOP}
                y2={baselineY}
                stroke="currentColor"
                strokeOpacity={0.25}
                strokeWidth={0.5}
                strokeDasharray="2 2"
              />
            )}
          </g>
        )}

        {data.map((bar) => {
          const cx = bar.bucket * slotW + slotW / 2;
          const x = cx - barW / 2;
          const totalH = (bar.total / max) * innerH;
          const isHovered = hover?.bucket === bar.bucket;
          const peerDimmed = hover !== null && !isHovered;

          // Stack segments bottom-to-top (used in bar mode only).
          let yCursor = baselineY;
          const rects: { y: number; h: number; key: string }[] = [];
          for (const seg of bar.segments) {
            const segH = (seg.count / max) * innerH;
            yCursor -= segH;
            rects.push({ y: yCursor, h: Math.max(0.5, segH), key: seg.key });
          }
          const topY = baselineY - totalH;
          // In line mode, anchor tooltip to the line's tallest point in
          // this bucket, not the stacked total.
          const lineTopY = (() => {
            if (style !== "line" || bar.segments.length === 0) return topY;
            let topCount = 0;
            for (const seg of bar.segments) {
              if (seg.count > topCount) topCount = seg.count;
            }
            return baselineY - (topCount / yMax) * innerH;
          })();
          const anchorY = style === "line" ? lineTopY : topY;

          return (
            <g
              key={bar.bucket}
              tabIndex={bar.total > 0 ? 0 : -1}
              role={bar.total > 0 ? "button" : undefined}
              aria-label={
                bar.total > 0
                  ? `CMC ${bar.bucket === 7 ? "7+" : bar.bucket}: ${formatCount(bar.total)} ${mode === "color" ? "pips" : "cards"}`
                  : undefined
              }
              className={cn(
                "transition-opacity duration-150",
                "focus:outline-none [&:focus-visible_.mc-bar-bg]:stroke-accent [&:focus-visible_.mc-bar-bg]:stroke-[1.5]",
                style === "bar" && peerDimmed ? "opacity-40" : "opacity-100",
              )}
              onMouseEnter={() => {
                if (bar.total === 0) return;
                const css = toCssCoords(cx, anchorY);
                setHover({ bucket: bar.bucket, cx: css.x, cy: css.y });
              }}
              onMouseLeave={() => setHover(null)}
              onFocus={() => {
                if (bar.total === 0) return;
                const css = toCssCoords(cx, anchorY);
                setHover({ bucket: bar.bucket, cx: css.x, cy: css.y });
              }}
              onBlur={() => setHover(null)}
            >
              {/* Invisible hit target across the full slot column —
                  makes hovering near (not exactly on) the bar work. */}
              <rect
                x={bar.bucket * slotW}
                y={PAD_TOP}
                width={slotW}
                height={H - PAD_TOP}
                fill="transparent"
              />

              {/* === BAR MODE: stack segments. The top segment gets
                  rounded top corners; line mode skips this entirely. === */}
              {style === "bar" && bar.segments.map((seg, i) => {
                const r = rects[i]!;
                const isTop = i === bar.segments.length - 1;
                const isBottom = i === 0;
                const fill = `url(#${uid}-grad-${seg.key})`;
                if (isTop && bar.segments.length > 1) {
                  // Top segment with rounded top corners only.
                  return (
                    <path
                      key={seg.key}
                      d={topRoundedRect(x, r.y, barW, r.h, 3.5)}
                      fill={fill}
                      filter={isHovered ? `url(#${uid}-glow)` : undefined}
                    />
                  );
                }
                if (isTop && isBottom) {
                  // Single-segment bar: round both top corners (only).
                  return (
                    <path
                      key={seg.key}
                      d={topRoundedRect(x, r.y, barW, r.h, 3.5)}
                      fill={fill}
                      filter={isHovered ? `url(#${uid}-glow)` : undefined}
                    />
                  );
                }
                return (
                  <rect
                    key={seg.key}
                    x={x}
                    y={r.y}
                    width={barW}
                    height={r.h}
                    fill={fill}
                    filter={isHovered ? `url(#${uid}-glow)` : undefined}
                  />
                );
              })}

              {/* Empty-bucket tick (bar mode only — line mode shows a
                  zero point on the line itself). */}
              {style === "bar" && bar.total === 0 && (
                <line
                  x1={x + 1}
                  x2={x + barW - 1}
                  y1={baselineY - 1.5}
                  y2={baselineY - 1.5}
                  stroke="currentColor"
                  strokeOpacity={0.25}
                  strokeWidth={1}
                  strokeLinecap="round"
                />
              )}
              {/* Bucket label */}
              <text
                x={cx}
                y={H - 5}
                textAnchor="middle"
                fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
                fontSize={9}
                letterSpacing={0.3}
                fill="currentColor"
                fillOpacity={0.55}
              >
                {bar.bucket === 7 ? "7+" : bar.bucket}
              </text>
              {/* Per-bar total label removed at this size — the tooltip
                  carries the count on hover; on-bar text reads as
                  chart-junk on a 96-tall canvas. */}
            </g>
          );
        })}
      </svg>

      {hover !== null && (() => {
        const bar = data.find((d) => d.bucket === hover.bucket);
        if (!bar || bar.total === 0) return null;
        return <Tooltip bar={bar} mode={mode} cx={hover.cx} cy={hover.cy} />;
      })()}
    </div>
  );
}

// Float a small surface card above the hovered bar. Anchored to the
// top-of-bar; translated up by 100% of its own height so it sits above
// the bar's total label.
function Tooltip({
  bar,
  mode,
  cx,
  cy,
}: {
  bar: BarData;
  mode: StackMode;
  cx: number;
  cy: number;
}) {
  // Reverse so the visual order matches the bar's top-down stacking:
  // top-most segment of the bar is the first row in the tooltip.
  const rows = [...bar.segments].reverse();
  return (
    <div
      role="tooltip"
      className={cn(
        "pointer-events-none absolute z-10",
        "surface px-2.5 py-2 shadow-lg",
        "min-w-[7.5rem]",
      )}
      style={{
        left: cx,
        top: cy,
        transform: "translate(-50%, calc(-100% - 12px))",
      }}
    >
      <div className="font-mono text-[10px] uppercase tracking-wider text-fg-subtle">
        CMC {bar.bucket === 7 ? "7+" : bar.bucket}
        <span className="ml-1.5 text-fg/80 normal-case">
          · {formatCount(bar.total)} {mode === "color" ? "pips" : "cards"}
        </span>
      </div>
      <ul className="mt-1.5 space-y-0.5">
        {rows.map((seg) => (
          <li
            key={seg.key}
            className="flex items-center justify-between gap-3 text-[11px]"
          >
            <span className="inline-flex items-center gap-1.5 text-fg">
              <span
                aria-hidden
                className="inline-block h-2 w-2 rounded-[2px]"
                style={{
                  background: `linear-gradient(180deg, ${seg.colorTop}, ${seg.colorBottom})`,
                }}
              />
              {seg.label}
            </span>
            <span className="font-mono tabular-nums text-fg-subtle">
              {formatCount(seg.count)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Rectangle path with rounded TOP corners only. Used for the topmost
// stack segment so the bar reads as a unified rounded column even
// though it's drawn from multiple flat-bottomed rects.
function topRoundedRect(x: number, y: number, w: number, h: number, r: number): string {
  const radius = Math.min(r, w / 2, h);
  return [
    `M ${x} ${y + radius}`,
    `A ${radius} ${radius} 0 0 1 ${x + radius} ${y}`,
    `L ${x + w - radius} ${y}`,
    `A ${radius} ${radius} 0 0 1 ${x + w} ${y + radius}`,
    `L ${x + w} ${y + h}`,
    `L ${x} ${y + h}`,
    "Z",
  ].join(" ");
}

function formatCount(n: number): string {
  // Pip counts can be fractional (hybrid 0.5). Render as integer when
  // possible to keep the chart looking clean.
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}
